import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { ChevronDown, ChevronUp } from "lucide-react";

function formatAddress(a) {
  if (!a) return "";
  return [
    a.line1,
    a.line2,
    a.landmark,
    a.city,
    a.district,
    a.state ? `${a.state}${a.pincode ? `-${a.pincode}` : ""}` : a.pincode,
  ]
    .map((p) => (typeof p === "string" ? p.trim() : p))
    .filter(Boolean)
    .join(", ");
}

// Persistent, collapsible summary of the booking-in-progress, pinned to the
// bottom of the screen. Reads the booking journey state from redux and shows
// only the fields that have data yet (Service, Mode, Booking for, Address).
export default function BookingDetailsSheet() {
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef(null);
  const navigoStore = useSelector((store) => store.navigo);
  const {
    currentServices,
    currentSelectedService,
    selectedBookingMode,
    selectedBookingProvider,
    familyMemberSelectedData,
    addressSelected,
  } = navigoStore.ui;

  const service =
    currentServices && currentServices.name
      ? currentServices.name
      : currentSelectedService?.name;

  const memberName = familyMemberSelectedData
    ? `${familyMemberSelectedData.firstName || ""} ${
        familyMemberSelectedData.lastName || ""
      }`.trim()
    : "";

  // Centre-visit picks a centre; other provider-based modes pick a provider.
  const providerLabel =
    selectedBookingMode === "Centre visit" ? "Centre" : "Provider";

  // Fields are listed in journey order; only those with data are shown, so the
  // floater naturally adapts per flow (e.g. Address for Home sample, Centre for
  // Centre visit).
  const fields = [
    { label: "Service", value: service },
    { label: "Mode", value: selectedBookingMode },
    { label: "Booking for", value: memberName },
    { label: providerLabel, value: selectedBookingProvider?.name },
    { label: "Address", value: formatAddress(addressSelected) },
  ].filter((f) => f.value);

  if (fields.length === 0) return null;

  return (
    <>
      <div
        onClick={() => setExpanded(false)}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(18, 25, 76, 0.45)",
          zIndex: 1000,
          opacity: expanded ? 1 : 0,
          pointerEvents: expanded ? "auto" : "none",
          transition: "opacity 0.25s ease",
        }}
      />
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1001,
          fontFamily: "'Montserrat', sans-serif",
          boxShadow: "0 -6px 20px rgba(0, 0, 0, 0.12)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          backgroundColor: "#FFFFFF",
        }}
      >
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          style={{
            width: "100%",
            minHeight: 54,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 20px",
            backgroundColor: "#E5E9F0",
            border: "none",
            cursor: "pointer",
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 700, color: "#12194C" }}>
            Booking details
          </span>
          {expanded ? (
            <ChevronDown size={22} color="#97144D" />
          ) : (
            <ChevronUp size={22} color="#97144D" />
          )}
        </button>

        <div
          style={{
            backgroundColor: "#FFFFFF",
            maxHeight: expanded ? (contentRef.current?.scrollHeight ?? 1000) : 0,
            opacity: expanded ? 1 : 0,
            overflow: "hidden",
            transition: "max-height 0.25s ease, opacity 0.25s ease",
          }}
        >
          <div ref={contentRef} style={{ padding: "4px 20px 20px" }}>
            {fields.map((f) => (
              <div
                key={f.label}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 16,
                  padding: "10px 0",
                }}
              >
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#97144D",
                    flexShrink: 0,
                  }}
                >
                  {f.label}:
                </span>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: "#12194C",
                    textAlign: "right",
                    lineHeight: 1.5,
                  }}
                >
                  {f.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
